import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import type { ActivationLifecycle } from '@/infra/activation'
import { type GetCurrentMemberFn } from '@/features/iam'
import { task } from 'mobx-task'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { computed } from 'mobx'
import { BillingMethodListItemViewModel } from '@/features/billing/profile/BillingMethodList/BillingMethodListItemViewModel'
import {
  type SnackbarFactory,
  type SnackbarRetryWrapperFactory,
} from '@/features/shell'
import { ignoreAbortError } from '@/utils/abort-util'

/**
 * Manages billing methods.
 */
export class BillingMethodListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Billing methods.
   */
  readonly methods

  constructor(
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    private readonly _billingProfileStore: BillingProfileStore,
  ) {
    this.methods = new ViewModelList({
      models: () => this.profile.billingMethods.slice(),
      create: (m) =>
        new BillingMethodListItemViewModel(
          this._billingProfileStore,
          this._newSnackbar,
          m,
        ),
    })
    this._lifecycle.manage(this.methods)
  }

  @computed
  get profile() {
    return this._billingProfileStore.getBillingProfileForMember(
      this._getCurrentMember(),
    )
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  fetchProfile = task(async (signal?: AbortSignal) => {
    const withRetry = this._createSnackbarRetryWrapper(
      'Unable to load payment methods',
      signal,
    )

    await withRetry(() =>
      this._billingProfileStore.fetchBillingProfileForMember(
        this._getCurrentMember(),
      ),
    ).catch(ignoreAbortError())
  })
}
